import { useCallback } from 'react'
import type { Dispatch } from 'react'
import type { CustomerLoginActionResult } from '../../../hooks/useCustomerAuthActions'
import type { CustomerSignupInput, CustomerTwoFactorChallengePayload } from '../../../types'
import type { AuthFlowAction } from './AuthFlow.types'

type UseAuthSubmitHandlersInput = {
  nextPath: string
  normalizedTwoFactorCode: string
  twoFactorChallenge: CustomerTwoFactorChallengePayload | null
  dispatch: Dispatch<AuthFlowAction>
  onClearError: () => void
  onNavigate: (targetPath: string) => void
  onLogin: (input: { identifier: string; password: string }) => Promise<CustomerLoginActionResult>
  onVerifyLoginSecondFactor: (input: { sessionId: string; code: string }) => Promise<CustomerLoginActionResult>
  onVerifyLoginSecondFactorWithPasskey: (input: { sessionId: string }) => Promise<CustomerLoginActionResult>
  onSignup: (input: CustomerSignupInput) => Promise<boolean>
  invalidTwoFactorCodeLabel: string
}

type UseAuthSubmitHandlersOutput = {
  handleLoginSubmit: (input: { identifier: string; password: string }) => Promise<CustomerLoginActionResult>
  handleSignupSubmit: (input: CustomerSignupInput) => Promise<boolean>
  handleTwoFactorChallenge: (session: CustomerTwoFactorChallengePayload) => void
  handleTwoFactorSubmit: () => Promise<void>
  handleTwoFactorPasskeySubmit: () => Promise<void>
  handleTwoFactorBack: () => void
  handleTwoFactorCodeChange: (value: string) => void
  handleTwoFactorCodeBlur: () => void
}

export const useAuthSubmitHandlers = ({
  nextPath,
  normalizedTwoFactorCode,
  twoFactorChallenge,
  dispatch,
  onClearError,
  onNavigate,
  onLogin,
  onVerifyLoginSecondFactor,
  onVerifyLoginSecondFactorWithPasskey,
  onSignup,
  invalidTwoFactorCodeLabel,
}: UseAuthSubmitHandlersInput): UseAuthSubmitHandlersOutput => {
  const handleLoginSubmit = useCallback(async (input: { identifier: string; password: string }) => {
    return onLogin(input)
  }, [onLogin])

  const handleSignupSubmit = useCallback(async (input: CustomerSignupInput) => {
    return onSignup(input)
  }, [onSignup])

  const handleTwoFactorChallenge = useCallback((session: CustomerTwoFactorChallengePayload) => {
    dispatch({ type: 'SET_TWO_FACTOR_CHALLENGE', challenge: session })
  }, [dispatch])

  const handleTwoFactorSubmit = useCallback(async () => {
    dispatch({ type: 'SET_TWO_FACTOR_TOUCHED', value: true })
    dispatch({ type: 'SET_TWO_FACTOR_ERROR', value: '' })

    if (!twoFactorChallenge || normalizedTwoFactorCode.length < 6) return

    dispatch({ type: 'SET_TWO_FACTOR_SUBMITTING', value: true })
    try {
      const result = await onVerifyLoginSecondFactor({
        sessionId: twoFactorChallenge.sessionId,
        code: normalizedTwoFactorCode,
      })

      if (result.kind === 'authenticated') {
        onNavigate(nextPath)
        return
      }

      dispatch({ type: 'SET_TWO_FACTOR_ERROR', value: invalidTwoFactorCodeLabel })
    } finally {
      dispatch({ type: 'SET_TWO_FACTOR_SUBMITTING', value: false })
    }
  }, [
    dispatch,
    invalidTwoFactorCodeLabel,
    nextPath,
    normalizedTwoFactorCode,
    onNavigate,
    onVerifyLoginSecondFactor,
    twoFactorChallenge,
  ])

  const handleTwoFactorPasskeySubmit = useCallback(async () => {
    dispatch({ type: 'SET_TWO_FACTOR_TOUCHED', value: true })
    dispatch({ type: 'SET_TWO_FACTOR_ERROR', value: '' })

    if (!twoFactorChallenge) return

    dispatch({ type: 'SET_TWO_FACTOR_SUBMITTING', value: true })
    try {
      const result = await onVerifyLoginSecondFactorWithPasskey({
        sessionId: twoFactorChallenge.sessionId,
      })

      if (result.kind === 'authenticated') {
        onNavigate(nextPath)
        return
      }

      dispatch({ type: 'SET_TWO_FACTOR_ERROR', value: invalidTwoFactorCodeLabel })
    } finally {
      dispatch({ type: 'SET_TWO_FACTOR_SUBMITTING', value: false })
    }
  }, [
    dispatch,
    invalidTwoFactorCodeLabel,
    nextPath,
    onNavigate,
    onVerifyLoginSecondFactorWithPasskey,
    twoFactorChallenge,
  ])

  const handleTwoFactorBack = useCallback(() => {
    dispatch({ type: 'CLEAR_TWO_FACTOR' })
    onClearError()
  }, [dispatch, onClearError])

  const handleTwoFactorCodeChange = useCallback((value: string) => {
    dispatch({ type: 'SET_TWO_FACTOR_CODE', value })
  }, [dispatch])

  const handleTwoFactorCodeBlur = useCallback(() => {
    dispatch({ type: 'SET_TWO_FACTOR_TOUCHED', value: true })
  }, [dispatch])

  return {
    handleLoginSubmit,
    handleSignupSubmit,
    handleTwoFactorChallenge,
    handleTwoFactorSubmit,
    handleTwoFactorPasskeySubmit,
    handleTwoFactorBack,
    handleTwoFactorCodeChange,
    handleTwoFactorCodeBlur,
  }
}
